/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { ReactNode } from 'react'

import { CategoryMultiSelect } from './CategoryMultiSelect'
import { ZOrderPicker } from './ZOrderPicker'

import type { GeofenceCategory, GeofenceFormValues } from '../types'
import { NAME_MAX_LENGTH } from '../validation'

const Field = ({
  label,
  htmlFor,
  aside,
  children,
}: {
  label: string
  htmlFor?: string
  aside?: ReactNode
  children: ReactNode
}) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-baseline justify-between">
      <label htmlFor={htmlFor} className="text-sm font-semibold">
        {label}
      </label>
      {aside}
    </div>
    {children}
  </div>
)

interface GeofenceFormProps {
  companyName: string
  values: GeofenceFormValues
  onChange: (values: GeofenceFormValues) => void
  /** Shown once the name has been touched or a save was tried. */
  nameError?: string
  onNameBlur: () => void
  categories: GeofenceCategory[]
}

/** The editor's left panel: what the geofence is called, which categories, which layer, and whether it shows. */
export const GeofenceForm = ({
  companyName,
  values,
  onChange,
  nameError,
  onNameBlur,
  categories,
}: GeofenceFormProps) => (
  <div className="flex flex-col gap-5">
    <Field label="Company">
      <p className="text-sm text-base-content/80 truncate" title={companyName}>
        {companyName}
      </p>
    </Field>

    <Field
      label="Name"
      htmlFor="geofence-name"
      aside={
        <span className={`text-xs ${values.name.length > NAME_MAX_LENGTH ? 'text-error' : 'text-base-content/50'}`}>
          {values.name.length}/{NAME_MAX_LENGTH}
        </span>
      }
    >
      <input
        id="geofence-name"
        type="text"
        className={`input input-bordered input-sm w-full ${nameError ? 'input-error' : ''}`}
        value={values.name}
        maxLength={NAME_MAX_LENGTH + 10}
        onChange={(e) => onChange({ ...values, name: e.target.value })}
        onBlur={onNameBlur}
        placeholder="e.g. Bega DC"
        aria-invalid={!!nameError}
        aria-describedby={nameError ? 'geofence-name-error' : undefined}
        autoFocus
      />
      {nameError && (
        <span id="geofence-name-error" className="text-xs text-error">
          {nameError}
        </span>
      )}
    </Field>

    <Field label="Category">
      <CategoryMultiSelect
        categories={categories}
        selected={values.categoryIds}
        onChange={(categoryIds) => onChange({ ...values, categoryIds })}
      />
    </Field>

    <Field label="Display Layout">
      <ZOrderPicker value={values.zorder} onChange={(zorder) => onChange({ ...values, zorder })} />
      <p className="text-xs text-base-content/60">Where geofences overlap, higher layers are drawn on top.</p>
    </Field>

    <Field label="Visibility">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-primary"
          checked={values.visible}
          onChange={(e) => onChange({ ...values, visible: e.target.checked })}
        />
        <span className="text-sm">Show on maps</span>
      </label>
      <p className="text-xs text-base-content/60">Hidden geofences aren’t drawn on the maps.</p>
    </Field>
  </div>
)
